import { updateDocument, createDocument, getId } from 'services/firestore'
import { uploadImage } from 'helpers'
import { updateParentHasManyRelationship } from 'hooks'
import { Technology } from 'models'
import { message } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslations } from 'contexts/Translation'

const useActionsTechnologyAdvancedForm = ({ initialData, form } = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const history = useHistory()
  const params = useParams()

  const prepareValues = async (values = {}, additionalValues = {}) => {
    const technologyId =
      initialData?.['technology']?._id || getId('technologies')
    const additionalReferences = { technologyId }
    const preparedValues = {
      _id: technologyId,
      ...additionalValues,
      name: values?.['technology']?.['name'] ?? null,
      description: values?.['technology']?.['description'] ?? null,
      imageUrl:
        (await uploadImage(values?.['technology']?.['imageUrl'])) ?? null
    }
    if (params.offerId) preparedValues['offerId'] = params.offerId
    return preparedValues
  }

  const saveForm = async (values, callback) => {
    try {
      // Prepare data to be saved
      const data = await prepareValues(values)
      // Updating parent references
      if (params.offerId)
        await updateParentHasManyRelationship(
          params.offerId,
          'offers',
          'technologyIds',
          data._id
        )
      // Save data
      if (initialData) {
        await updateDocument('technologies', initialData.technology?._id, data)
        message.success(t('Technology successfully updated'))
      } else {
        await createDocument('technologies', data, data._id)
        message.success(t('Technology successfully created'))
      }
      // Final callback
      callback?.()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Something went wrong during data save'))
    }
  }

  const validateForm = (values) => {
    try {
      // Prepare data to be validated
      const validationData = {
        name: values?.['technology']?.['name'],
        description: values?.['technology']?.['description'],
        imageUrl: values?.['technology']?.['imageUrl']
      }
      Technology.validationSchema.validateSync(validationData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Technology validation error: ') + t(error.message))
    }
  }

  const onFinish = async () => {
    if (loading) return // Avoid multiple calls

    try {
      setLoading(true)
      // Get form values
      const formValues = form.getFieldsValue()
      // Validate fields
      validateForm(formValues)
      // Final callback
      const callback = () => history.goBack()
      // Save data
      await saveForm(formValues, callback)
    } catch (error) {
      setLoading(false)
      message.error(error.message)
    }
  }

  const onReset = () => {
    form.resetFields()
    history.goBack()
  }

  return { onFinish, onReset, loading, saveForm, validateForm, prepareValues }
}

export default useActionsTechnologyAdvancedForm
